import { useState, useEffect } from "react";
import { Layout } from "@/components/layout/Layout";
import { LoadingLabel, Skel } from "@/components/home/TenantSkeletons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/hooks/use-toast";
import { format, getDay } from "date-fns";
import { Calendar as CalendarIcon, Clock, Send, Check, Loader2, Sparkles, MapPin, ArrowRight, Banknote, Wallet, CreditCard } from "lucide-react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { cn, renderDisplayText } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useOrg } from "@/contexts/OrgContext";
import { useAuth } from "@/contexts/AuthContext";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { AddressAutocomplete, type PlaceSelection } from "@/components/ui/address-autocomplete";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CallbackDialog } from "@/components/CallbackDialog";
import { formatMoney, parseMoneyValue } from "@/lib/currency";
import { buildPaypalMeUrl } from "@/lib/paypal";
import { getActiveCustomerPaymentMethods, useOrgSettings } from "@/hooks/useOrgSettings";
import { PageSeo } from "@/components/seo/PageSeo";
import { DEFAULT_TIMEZONE, nowInTimeZone } from "@/lib/timezones";
import { DEMO_SHOWCASE } from "@/config/demoShowcase";
import { useBookingConfirmation } from "@/hooks/useBookingConfirmation";
import { bookingModeNote, bookingSuccessCopy, readBookingOutcome, type BookingOutcome } from "@/lib/bookingConfirmation";
import { useBookingCardPayments, startBookingCheckout } from "@/hooks/useBookingPayment";
import {
  BOOKING_PAY_METHOD_RPC_VALUE,
  amountDueSummary,
  availableBookingPaymentMethods,
  bookingAmountDue,
  bookingSubmitLabel,
  pickBookingPaymentMethod,
  readPendingBookingPayment,
  savePendingBookingPayment,
  type BookingPayMethod,
} from "@/lib/bookingPayment";
import { BookingPaymentReturn, type BookingPaymentReturnMode } from "@/components/booking/BookingPaymentReturn";

interface ServiceVariant {
  id: string;
  service_id: string;
  name: string;
  duration: string;
  price: string;
  sort_order: number;
}

interface Service {
  id: string;
  name: string;
  duration: string;
  price: string;
  is_quote_only: boolean;
  resource_required: number;
  variants?: ServiceVariant[];
}

interface BusinessHour {
  day_of_week: number;
  is_open: boolean;
  open_time: string;
  close_time: string;
}

/**
 * Sanitised booking availability returned by get_org_booking_availability.
 * One row represents one available team-member window, but exposes no member identity.
 */
interface TeamAvailability {
  day_of_week: number;
  start_time: string;
  end_time: string;
}

/** A non-identifying busy window returned by `get_org_busy_intervals`. */
interface BusyInterval {
  start_time: string;
  end_time: string;
}

interface BookingRequestResult {
  bookingId?: string;
  seriesId?: string | null;
  /** 'instant' when the business auto-confirms and the slot was still free; 'payment' for a card hold. */
  confirmation?: 'instant' | 'request' | 'payment';
  status?: string;
  /** Pay to book: how this fixed-price booking is being paid. */
  payment?: {
    method: BookingPayMethod;
    status: string;
    total: number;
    dueNow: number;
    currency: string;
    holdExpiresAt: string | null;
    /** One-time token for this booking's card payment (card only). */
    token: string | null;
  } | null;
}

/** What the success card shows after a booking goes through. */
interface BookingConfirmationState {
  outcome: BookingOutcome;
  isRepeat: boolean;
  when: string;
  /** Set when the customer chose PayPal. */
  paypalUrl: string | null;
}

interface EnquiryRequestResult {
  enquiryId?: string;
}

// "How often?" on the booking form. Anything but one-off creates a repeat
// clean server-side (see submit_customer_booking_request).
const BOOKING_FREQUENCIES = [
  { value: "one-off", label: "Just this once" },
  { value: "weekly", label: "Every week" },
  { value: "fortnightly", label: "Every 2 weeks" },
  { value: "four-weekly", label: "Every 4 weeks" },
  { value: "monthly", label: "Once a month" },
] as const;

type CustomerRequestRpc = {
  rpc(
    name: 'submit_customer_booking_request',
    args: Record<string, string>
  ): Promise<{ data: BookingRequestResult | null; error: { message?: string; hint?: string; code?: string } | null }>;
  rpc(
    name: 'submit_customer_enquiry',
    args: Record<string, string>
  ): Promise<{ data: EnquiryRequestResult | null; error: unknown }>;
};

type BusyIntervalsRpc = {
  rpc(
    name: 'get_org_busy_intervals',
    args: { _organization_id: string; _scheduled_date: string }
  ): Promise<{ data: BusyInterval[] | null; error: unknown }>;
};

type BookingAvailabilityRpc = {
  rpc(
    name: 'get_org_booking_availability',
    args: { _organization_id: string }
  ): Promise<{ data: TeamAvailability[] | null; error: unknown }>;
};


interface OrgLocation {
  latitude: number | null;
  longitude: number | null;
  serviceRadiusKm: number | null;
}

/** Great-circle distance between two lat/lng points, in kilometres. */
const distanceKm = (
  a: { latitude: number; longitude: number },
  b: { latitude: number; longitude: number },
): number => {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const earthRadiusKm = 6371;
  const dLat = toRad(b.latitude - a.latitude);
  const dLng = toRad(b.longitude - a.longitude);
  const lat1 = toRad(a.latitude);
  const lat2 = toRad(b.latitude);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * earthRadiusKm * Math.asin(Math.sqrt(h));
};

const normalizeAreaText = (value: string): string =>
  value
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

const matchesNamedServiceArea = (address: string, areas: string[]): boolean => {
  const normalizedAddress = normalizeAreaText(address);
  if (!normalizedAddress) return false;

  return areas.some((area) => {
    const normalizedArea = normalizeAreaText(area);
    if (!normalizedArea) return false;

    return (
      normalizedAddress === normalizedArea ||
      normalizedAddress.startsWith(`${normalizedArea},`) ||
      normalizedAddress.startsWith(`${normalizedArea} `) ||
      normalizedAddress.includes(`, ${normalizedArea},`) ||
      normalizedAddress.endsWith(`, ${normalizedArea}`)
    );
  });
};

/** "HH:MM[:SS]" → minutes since midnight. */
const timeToMinutes = (value: string): number => {
  const [hours, minutes] = value.substring(0, 5).split(':').map(Number);
  return hours * 60 + minutes;
};

/** Minutes since midnight → "HH:MM" (wraps at 24h). */
const minutesToTime = (value: number): string => {
  const wrapped = ((value % 1440) + 1440) % 1440;
  const hours = Math.floor(wrapped / 60);
  const minutes = wrapped % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

/**
 * Parse a free-text service duration ("1 hour 30 minutes", "45 mins", "2 hrs")
 * into minutes. Defaults to 60 when no recognisable amount is present.
 */
const parseDurationMinutes = (value: string): number => {
  const text = (value || '').toLowerCase();
  const hourMatch = text.match(/(\d+(?:\.\d+)?)\s*(?:hours|hour|hrs|hr|h)\b/);
  const minuteMatch = text.match(/(\d+)\s*(?:minutes|minute|mins|min|m)\b/);
  let total = 0;
  if (hourMatch) total += Math.round(parseFloat(hourMatch[1]) * 60);
  if (minuteMatch) total += parseInt(minuteMatch[1], 10);
  return total > 0 ? total : 60;
};

const BookingPage = () => {
  const { orgId, loading: orgLoading } = useOrg();
  const { isOrgOwner } = useAuth();
  const { pathname } = useLocation();
  const isDemoRoute = pathname === '/demo/booking' || pathname.startsWith('/demo/');
  const [isBookingSubmitting, setIsBookingSubmitting] = useState(false);
  // Set after a successful booking: instant confirmation vs request sent.
  const [bookingConfirmation, setBookingConfirmation] = useState<BookingConfirmationState | null>(null);
  const [services, setServices] = useState<Service[]>([]);
  const [loadingServices, setLoadingServices] = useState(true);
  const [businessHours, setBusinessHours] = useState<BusinessHour[]>([]);
  const [teamAvailability, setTeamAvailability] = useState<TeamAvailability[]>([]);
  const [busyIntervals, setBusyIntervals] = useState<BusyInterval[]>([]);
  const [serviceAreas, setServiceAreas] = useState<string[]>([]);
  const [bufferMinutes, setBufferMinutes] = useState(0);
  // The tenant's IANA timezone. Booking days/slots are anchored to the
  // business's clock, not the visiting browser's. Defaults to Europe/London for
  // tenants provisioned before timezone storage existed.
  const [orgTimezone, setOrgTimezone] = useState<string>(DEFAULT_TIMEZONE);
  const [orgLocation, setOrgLocation] = useState<OrgLocation>({
    latitude: null,
    longitude: null,
    serviceRadiusKm: null,
  });
  const [paymentChoice, setPaymentChoice] = useState<BookingPayMethod | ''>('');
  // Back from Stripe Checkout (?payment=success|cancelled&booking=…), or we
  // couldn't open it: the payment panel replaces the success card.
  const [searchParams, setSearchParams] = useSearchParams();
  const [paymentReturn, setPaymentReturn] = useState<{ mode: BookingPaymentReturnMode; bookingId: string; openError?: string | null } | null>(() => {
    const mode = searchParams.get('payment');
    const booking = searchParams.get('booking');
    return (mode === 'success' || mode === 'cancelled') && booking ? { mode, bookingId: booking } : null;
  });
  const [selectedService, setSelectedService] = useState<string>("");
  const [selectedVariant, setSelectedVariant] = useState<string>("");
  const [date, setDate] = useState<Date>();
  const [time, setTime] = useState<string>("");
  const [addressOutsideArea, setAddressOutsideArea] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    sqm: "",
    frequency: "one-off",
    message: "",
  });
  const { data: orgSettings } = useOrgSettings(orgId);
  const { data: confirmationSettings } = useBookingConfirmation(orgId);
  const confirmationMode = confirmationSettings?.mode ?? 'approval';
  const currency = orgSettings?.currency;
  const paymentPolicy = orgSettings?.paymentPolicy;
  const activePaymentMethods = getActiveCustomerPaymentMethods(orgSettings?.paymentMethods ?? {
    cash_in_person: { enabled: true, available: true },
    stripe: { enabled: false, available: false },
    paypal: { enabled: false, available: false },
  });
  const paypalHandle = orgSettings?.paymentMethods.paypal.paypal_me;
  const paypalOffered = activePaymentMethods.some(([key]) => key === 'paypal') && Boolean(paypalHandle);
  const cashOffered = activePaymentMethods.some(([key]) => key === 'cash_in_person');
  const { data: cardPayments } = useBookingCardPayments(orgId);
  const bookingReturnPath = isDemoRoute ? '/demo/booking' : '/booking';

  useEffect(() => {
    if (!orgId || orgLoading) return;
    fetchServices();
    fetchBusinessHours();
    fetchTeamAvailability();
    fetchServiceAreas();
    fetchOrgLocation();
  // Load booking setup once for the resolved org; the fetch helpers all read the same orgId.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, orgLoading]);

  const fetchServices = async () => {
    try {
      const { data: servicesData, error: servicesError } = await supabase
        .from('services')
        .select('id, name, duration, price, is_quote_only, resource_required')
        .eq('organization_id', orgId)
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (servicesError) throw servicesError;

      const { data: variantsData, error: variantsError } = await supabase
        .from('service_variants')
        .select('*')
        .eq('organization_id', orgId)
        .order('sort_order', { ascending: true });

      if (variantsError) throw variantsError;

      const servicesWithVariants = (servicesData || []).map(service => ({
        ...service,
        is_quote_only: service.is_quote_only ?? false,
        resource_required: service.resource_required ?? 1,
        variants: (variantsData || []).filter(v => v.service_id === service.id),
      }));

      setServices(servicesWithVariants);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoadingServices(false);
    }
  };

  const fetchBusinessHours = async () => {
    try {
      const { data, error } = await supabase
        .from('business_hours')
        .select('day_of_week, is_open, open_time, close_time')
        .eq('organization_id', orgId);

      if (error) throw error;
      setBusinessHours(data || []);
    } catch (error) {
      console.error('Error fetching business hours:', error);
    }
  };

  const fetchTeamAvailability = async () => {
    try {
      // The public booking page cannot read team_members/team_availability directly:
      // those tables are protected by RLS and anonymous reads return empty/denied.
      // This RPC returns only non-identifying availability windows for slot maths.
      const { data, error } = await (supabase as unknown as BookingAvailabilityRpc).rpc(
        'get_org_booking_availability',
        { _organization_id: orgId },
      );
      if (error) throw error;
      setTeamAvailability(data || []);
    } catch (error) {
      console.error('Error fetching team availability:', error);
      setTeamAvailability([]);
    }
  };

  const fetchServiceAreas = async () => {
    try {
      const { data, error } = await supabase
        .from('company_info')
        .select('service_areas, buffer_minutes, timezone')
        .eq('organization_id', orgId)
        .maybeSingle();

      if (error) throw error;
      setServiceAreas(data?.service_areas || []);
      setBufferMinutes(data?.buffer_minutes ?? 0);
      setOrgTimezone(data?.timezone || DEFAULT_TIMEZONE);
    } catch (error) {
      console.error('Error fetching service areas:', error);
    }
  };

  // Busy windows for the selected date, used to subtract already-booked
  // capacity from the offered slots. Uses the anon-safe RPC (the `jobs` table
  // is not readable by anonymous bookers and would expose customer PII).
  const fetchBusyIntervals = async (selectedDate: Date) => {
    try {
      const { data, error } = await (supabase as unknown as BusyIntervalsRpc).rpc(
        'get_org_busy_intervals',
        {
          _organization_id: orgId,
          _scheduled_date: format(selectedDate, 'yyyy-MM-dd'),
        },
      );
      if (error) throw error;
      setBusyIntervals(data || []);
    } catch (error) {
      console.error('Error fetching busy intervals:', error);
      setBusyIntervals([]);
    }
  };

  const fetchOrgLocation = async () => {
    try {
      const { data, error } = await supabase
        .from('organizations')
        .select('latitude, longitude, service_radius_km')
        .eq('id', orgId)
        .maybeSingle();

      if (error) throw error;
      setOrgLocation({
        latitude: data?.latitude ?? null,
        longitude: data?.longitude ?? null,
        serviceRadiusKm: data?.service_radius_km ?? null,
      });
    } catch (error) {
      console.error('Error fetching org location:', error);
    }
  };

  // Check if an address is within the service area. Named locality selections
  // like "Cardiff, UK" should be accepted when Cardiff is configured, while
  // street names such as "5 Cardiff Street, Bristol" still rely on the radius.
  // For geocoded addresses outside an exact locality match, the travel radius
  // (service_radius_km around the org centre) remains authoritative. Otherwise
  // we fall back to matching the configured locality names.
  const checkAddressInServiceArea = (
    address: string,
    coords?: { latitude: number; longitude: number } | null,
  ): boolean => {
    if (!address || serviceAreas.length === 0) return true;
    if (matchesNamedServiceArea(address, serviceAreas)) return true;

    if (
      coords &&
      orgLocation.latitude != null &&
      orgLocation.longitude != null &&
      orgLocation.serviceRadiusKm != null
    ) {
      const center = { latitude: orgLocation.latitude, longitude: orgLocation.longitude };
      return distanceKm(center, coords) <= orgLocation.serviceRadiusKm;
    }

    return serviceAreas.some(area => normalizeAreaText(address).includes(normalizeAreaText(area)));
  };

  const handleAddressChange = (value: string) => {
    setFormData(prev => ({ ...prev, address: value }));
    if (value.length > 10) {
      const isInArea = checkAddressInServiceArea(value);
      setAddressOutsideArea(!isInArea);
    } else {
      setAddressOutsideArea(false);
    }
  };

  const handlePlaceSelect = (place: PlaceSelection) => {
    const coords =
      place.latitude != null && place.longitude != null
        ? { latitude: place.latitude, longitude: place.longitude }
        : null;
    setAddressOutsideArea(!checkAddressInServiceArea(place.formattedAddress, coords));
  };

  const selectedServiceData = services.find(s => s.id === selectedService);
  const selectedVariantData = selectedServiceData?.variants?.find(v => v.id === selectedVariant);

  // The job's length drives slot spacing and the booking end time. Prefer the
  // chosen variant's duration, falling back to the service's. Stored as free
  // text (e.g. "1 hour 30 minutes", "45 mins"), so parse it into minutes.
  const serviceDurationMinutes = parseDurationMinutes(
    selectedVariantData?.duration ?? selectedServiceData?.duration ?? '',
  );
  // How many team members a single booking of this service occupies (capacity).
  const requiredResources = Math.max(1, selectedServiceData?.resource_required ?? 1);

  // "Now" in the tenant's timezone — the authoritative clock for which days and
  // slots are still bookable, independent of where the visitor's browser is.
  const tenantNow = nowInTimeZone(orgTimezone);

  // Get available time slots for the selected date, stepping by the service
  // duration + buffer and only offering a slot when enough team members are free
  // for the whole interval to satisfy the service's required resource count.
  const getAvailableTimeSlots = () => {
    if (!date || !selectedServiceData) return [];

    const dayOfWeek = getDay(date);
    // When the chosen day is "today" in the tenant's timezone, only offer slots
    // that start strictly after the tenant's current wall-clock time.
    const selectedDateStr = format(date, 'yyyy-MM-dd');
    const isTenantToday = selectedDateStr === tenantNow.todayStr;
    const businessHour = businessHours.find(bh => bh.day_of_week === dayOfWeek);
    if (!businessHour || !businessHour.is_open) return [];

    const dayAvailability = teamAvailability.filter(ta => ta.day_of_week === dayOfWeek);
    if (dayAvailability.length === 0) return [];

    const openMinutes = timeToMinutes(businessHour.open_time);
    const closeMinutes = timeToMinutes(businessHour.close_time);
    const step = serviceDurationMinutes + bufferMinutes;

    const slots: string[] = [];
    for (let start = openMinutes; start + serviceDurationMinutes <= closeMinutes; start += step) {
      const end = start + serviceDurationMinutes;
      const capacity = dayAvailability.filter(ta =>
        timeToMinutes(ta.start_time) <= start && timeToMinutes(ta.end_time) >= end
      ).length;
      // Each already-booked job overlapping this interval consumes one unit of
      // capacity (public bookings are unassigned, so capacity is counted in
      // aggregate rather than per member).
      const booked = busyIntervals.filter(b =>
        timeToMinutes(b.start_time) < end && timeToMinutes(b.end_time) > start
      ).length;
      if (capacity - booked >= requiredResources && (!isTenantToday || start > tenantNow.minutes)) {
        slots.push(minutesToTime(start));
      }
    }

    return slots;
  };

  const availableTimeSlots = getAvailableTimeSlots();
  const isQuoteOnly = selectedServiceData?.is_quote_only ?? false;
  const hasVariants = (selectedServiceData?.variants?.length || 0) > 0;

  // Pay to book: fixed-price cleans show the ways this business takes payment
  // and what's due now (full price or the deposit from the payment policy).
  const servicePrice = isQuoteOnly
    ? null
    : parseMoneyValue(hasVariants ? selectedVariantData?.price : selectedServiceData?.price);
  const bookingAmounts = bookingAmountDue(servicePrice, paymentPolicy);
  const offeredPaymentMethods = availableBookingPaymentMethods(
    { card: cardPayments?.card === true, cash: cashOffered, paypal: paypalOffered },
    { fixedPrice: bookingAmounts.total > 0 },
  );
  const chosenPaymentMethod = pickBookingPaymentMethod(paymentChoice, offeredPaymentMethods);
  const currencySymbol = currency?.symbol ?? '£';

  const sendCustomerAccessLink = async (email: string) => {
    const { error } = await supabase.functions.invoke('send-customer-magic-link', {
      body: { email, organizationId: orgId },
    });

    if (error) {
      console.error('Customer access link failed:', error);
    }
  };

  const handleServiceSelect = (serviceId: string) => {
    setSelectedService(serviceId);
    setSelectedVariant("");
    setDate(undefined);
    setTime("");
  };

  useEffect(() => {
    setTime("");
    if (!orgId || !date) {
      setBusyIntervals([]);
      return;
    }
    fetchBusyIntervals(date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, orgId]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleBookingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsBookingSubmitting(true);
    if (!selectedService || !date || !time) {
      toast({
        title: "Please complete all fields",
        description: "Select a service, date, and time to book.",
        variant: "destructive",
      });
      setIsBookingSubmitting(false);
      return;
    }
    if (hasVariants && !selectedVariant) {
      toast({
        title: "Please select an option",
        description: "Choose a service option to continue.",
        variant: "destructive",
      });
      setIsBookingSubmitting(false);
      return;
    }
    if (!formData.name || !formData.email || !formData.phone || !formData.address) {
      toast({
        title: "Please fill in all contact details",
        description: "Name, email, phone, and address are required.",
        variant: "destructive",
      });
      setIsBookingSubmitting(false);
      return;
    }

    try {
      const variant = selectedServiceData?.variants?.find(v => v.id === selectedVariant);
      const serviceName = selectedServiceData?.name || 'Cleaning Service';
      const variantName = variant ? ` – ${variant.name}` : '';
      const bookingDate = format(date, 'PPP');

      const { data: result, error } = await (supabase as unknown as CustomerRequestRpc).rpc('submit_customer_booking_request', {
        _organization_id: orgId,
        _name: formData.name,
        _email: formData.email,
        _phone: formData.phone,
        _address: formData.address,
        _service_id: selectedService,
        _service_name: `${serviceName}${variantName}`,
        _frequency: formData.frequency,
        _premises_size: formData.sqm || 'Not specified',
        _preferred_date: format(date, 'yyyy-MM-dd'),
        _preferred_time: time,
        _end_time: minutesToTime(timeToMinutes(time) + serviceDurationMinutes),
        _payment_method: chosenPaymentMethod ? BOOKING_PAY_METHOD_RPC_VALUE[chosenPaymentMethod] : 'cash_in_person',
        _message: `Booking for ${bookingDate} at ${time}${formData.message ? `. Notes: ${formData.message}` : ''}`,
        // The price is read server-side from the chosen option.
        ...(selectedVariant ? { _variant_id: selectedVariant } : {}),
      });

      if (error) throw error;

      // Card: the slot is held while they pay. Off to Stripe Checkout; the
      // emails go out once the payment is confirmed.
      if (result?.payment?.method === 'card' && result.payment.token && result.bookingId) {
        savePendingBookingPayment(window.sessionStorage, {
          bookingId: result.bookingId,
          token: result.payment.token,
          email: formData.email,
          when: `on ${bookingDate} at ${time}`,
          isRepeat: Boolean(result.seriesId),
        });
        try {
          const { url } = await startBookingCheckout({
            bookingId: result.bookingId,
            token: result.payment.token,
            returnPath: bookingReturnPath,
          });
          window.location.assign(url);
        } catch (checkoutError) {
          setPaymentReturn({ mode: 'cancelled', bookingId: result.bookingId, openError: (checkoutError as Error).message });
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
        return;
      }
      // A weekly/fortnightly/4-weekly/monthly booking creates a repeat clean
      // server-side (pending until the first visit is confirmed — straight away
      // when the business confirms instantly).
      const isRepeat = Boolean(result?.seriesId);
      // The server has the final say: an instant-confirm business can still
      // return a request when the slot couldn't be vouched for.
      const outcome = readBookingOutcome(result);

      // Send confirmation email (recipient/content resolved server-side from the record)
      try {
        await supabase.functions.invoke('send-enquiry-confirmation', {
          body: {
            recordId: result?.bookingId,
            source: 'job',
            organizationId: orgId,
          },
        });
      } catch (emailError) {
        console.error('Email notification failed:', emailError);
        // Don't fail the submission if email fails
      }

      // Notify the tenant owner of the new booking (recipient resolved server-side)
      try {
        await supabase.functions.invoke('send-owner-notification', {
          body: {
            recordId: result?.bookingId,
            source: 'job',
            organizationId: orgId,
          },
        });
      } catch (notifyError) {
        console.error('Owner notification failed:', notifyError);
        // Don't fail the submission if owner notification fails
      }

      await sendCustomerAccessLink(formData.email);

      let paypalUrl: string | null = null;
      if (chosenPaymentMethod === 'paypal' && paypalOffered) {
        // The amount due now (deposit or full price); the plain PayPal.me page if unknown.
        const price = bookingAmounts.dueNow > 0 ? bookingAmounts.dueNow : parseMoneyValue(variant?.price ?? selectedServiceData?.price);
        paypalUrl = buildPaypalMeUrl(paypalHandle, price, currency?.code ?? 'GBP') ?? `https://paypal.me/${paypalHandle}`;
      }
      const when = `on ${bookingDate} at ${time}`;
      setBookingConfirmation({ outcome, isRepeat, when, paypalUrl });
      window.scrollTo({ top: 0, behavior: 'smooth' });

      const copy = bookingSuccessCopy(outcome, { isRepeat, when });
      toast({ title: copy.toastTitle, description: copy.body });

      // Reset form
      setSelectedService("");
      setSelectedVariant("");
      setDate(undefined);
      setTime("");
      setFormData({
        name: "",
        email: "",
        phone: "",
        address: "",
        sqm: "",
        frequency: "one-off",
        message: "",
      });
    } catch (error) {
      console.error('Error submitting booking:', error);
      // Our own plain-English refusals (payment needed, slot gone) are worth showing.
      const e = error as { message?: string; hint?: string; code?: string } | null;
      const ours = e?.hint === 'payment_required' || e?.code === '23514';
      toast({
        title: ours ? "We couldn't book that" : "Something went wrong",
        description: ours && e?.message ? e.message : "Please try again or contact us directly.",
        variant: "destructive",
      });
    } finally {
      setIsBookingSubmitting(false);
    }
  };

  const handleQuoteSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedService) {
      toast({
        title: "Please select a service",
        description: "Choose a service type to continue.",
        variant: "destructive",
      });
      return;
    }
    if (!formData.name || !formData.email || !formData.phone || !formData.address) {
      toast({
        title: "Please fill in all required fields",
        description: "Name, email, phone, and address are required.",
        variant: "destructive",
      });
      return;
    }

    try {
      const premisesSize = formData.sqm ? `${formData.sqm} sqm` : "Not specified";

      const { data: result, error } = await (supabase as unknown as CustomerRequestRpc).rpc('submit_customer_enquiry', {
        _organization_id: orgId,
        _name: formData.name,
        _email: formData.email,
        _phone: formData.phone,
        _address: formData.address,
        _service_id: selectedService,
        _frequency: formData.frequency,
        _premises_size: premisesSize,
        _subject: selectedServiceData?.name ? `Quote request: ${selectedServiceData.name}` : 'Quote request',
        _message: formData.message,
      });

      if (error) throw error;

      // Send confirmation email (recipient/content resolved server-side from the record)
      try {
        await supabase.functions.invoke('send-enquiry-confirmation', {
          body: {
            recordId: result?.enquiryId,
            source: 'enquiry',
            organizationId: orgId,
          },
        });
      } catch (emailError) {
        console.error('Email notification failed:', emailError);
        // Don't fail the submission if email fails
      }

      // Notify the tenant owner of the new enquiry (recipient resolved server-side)
      try {
        await supabase.functions.invoke('send-owner-notification', {
          body: {
            recordId: result?.enquiryId,
            source: 'enquiry',
            organizationId: orgId,
          },
        });
      } catch (notifyError) {
        console.error('Owner notification failed:', notifyError);
        // Don't fail the submission if owner notification fails
      }

      await sendCustomerAccessLink(formData.email);

      toast({
        title: "Quote request received!",
        description: "We'll get back to you within 24 hours. We’ve also emailed you a private link to view messages.",
      });

      // Reset form
      setSelectedService("");
      setFormData({
        name: "",
        email: "",
        phone: "",
        address: "",
        sqm: "",
        frequency: "one-off",
        message: "",
      });
    } catch (error) {
      console.error('Error submitting enquiry:', error);
      toast({
        title: "Something went wrong",
        description: "Please try again or contact us directly.",
        variant: "destructive",
      });
    }
  };

  return (
    <Layout>
      <PageSeo pageSlug="booking" fallbackTitle="Book a clean" fallbackDescription="Book or request a cleaning service." />
      {/* Hero */}
      <section className="relative -mt-20 overflow-hidden pb-10 pt-32 md:pb-14 md:pt-40">
        <div className="tp-hero-wash" aria-hidden />
        <div className="container relative mx-auto px-5 text-center md:px-6">
          <p className="tp-eyebrow">Online booking</p>
          <h1 className="tp-display tp-display--md mt-6">
            {renderDisplayText("Book my cleaning")}
          </h1>
          <p className="tp-lead mx-auto mt-5 max-w-2xl">
            {isDemoRoute
              ? DEMO_SHOWCASE.booking.lead
              : confirmationMode === 'instant'
                ? "Pick a clean and a free slot, and you're booked in straight away."
                : "Pick a clean and a time that suits. We'll confirm it with you shortly."}
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="pb-24 bg-background">
        <div className="container mx-auto px-4">
          {paymentReturn && (
            <BookingPaymentReturn
              key={`${paymentReturn.mode}-${paymentReturn.bookingId}`}
              mode={paymentReturn.mode}
              bookingId={paymentReturn.bookingId}
              pending={readPendingBookingPayment(window.sessionStorage, paymentReturn.bookingId)}
              orgId={orgId}
              returnPath={bookingReturnPath}
              currencySymbol={currencySymbol}
              timeZone={orgTimezone}
              openError={paymentReturn.openError}
              onDone={() => {
                setPaymentReturn(null);
                if (searchParams.has('payment') || searchParams.has('booking')) {
                  const next = new URLSearchParams(searchParams);
                  next.delete('payment');
                  next.delete('booking');
                  setSearchParams(next, { replace: true });
                }
              }}
            />
          )}
          {!paymentReturn && bookingConfirmation && (() => {
            const copy = bookingSuccessCopy(bookingConfirmation.outcome, {
              isRepeat: bookingConfirmation.isRepeat,
              when: bookingConfirmation.when,
            });
            return (
              <Card
                className="tp-card max-w-3xl mx-auto mb-6"
                role="status"
                aria-label="Booking confirmation"
                data-outcome={bookingConfirmation.outcome}
              >
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-xl">
                    {bookingConfirmation.outcome === 'instant'
                      ? <Check className="h-5 w-5 text-primary" />
                      : <Clock className="h-5 w-5 text-primary" />}
                    {copy.heading}
                  </CardTitle>
                  <CardDescription>
                    {copy.body}
                    {bookingConfirmation.paypalUrl && ' You chose to pay with PayPal, so you can pay securely now.'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col gap-3 sm:flex-row">
                  {bookingConfirmation.paypalUrl && (
                    <Button asChild variant="hero" size="lg" className="w-full sm:w-auto">
                      <a href={bookingConfirmation.paypalUrl} target="_blank" rel="noopener noreferrer">
                        <Wallet className="w-4 h-4 mr-2" /> Pay with PayPal
                      </a>
                    </Button>
                  )}
                  <Button type="button" variant="outline" size="lg" className="w-full sm:w-auto" onClick={() => setBookingConfirmation(null)}>
                    {bookingConfirmation.paypalUrl ? 'Dismiss' : 'Book another clean'}
                  </Button>
                </CardContent>
              </Card>
            );
          })()}
          <Card className="tp-card max-w-3xl mx-auto">
            <CardHeader>
              <CardTitle className="text-xl">Select your service</CardTitle>
              <CardDescription>
                Choose from our range of professional cleaning services.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Service Selection - Always visible */}
              <div className="space-y-3 mb-8">
                {loadingServices ? (
                  <div className="tp-skeleton grid grid-cols-2 gap-3 sm:grid-cols-3" aria-busy="true">
                    <LoadingLabel>Loading services</LoadingLabel>
                    {[0, 1, 2, 3, 4, 5].map((index) => (
                      <div key={index} className={cn("flex flex-col items-center rounded-xl border-2 border-border p-4", index >= 4 && "hidden sm:flex")} aria-hidden>
                        <Skel className="mb-2 h-10 w-10 rounded-lg" />
                        <Skel className="h-3 w-3/4 tp-skel-round" />
                        <Skel className="mt-2 h-3 w-1/2 tp-skel-round" />
                      </div>
                    ))}
                  </div>
                ) : services.length === 0 ? (
                  <div className="text-center py-8 space-y-2">
                    {isOrgOwner ? (
                      <>
                        <p className="text-muted-foreground">No services added yet.</p>
                        <Link to="/admin/services" className="text-primary text-sm font-medium underline underline-offset-4 hover:text-primary/80">
                          Add your services in the admin →
                        </Link>
                      </>
                    ) : isDemoRoute ? (
                      <p className="text-muted-foreground">No services on the demo site yet — add some from the admin dashboard.</p>
                    ) : (
                      <p className="text-muted-foreground">No services available at the moment. Please check back soon.</p>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {services.map((service) => (
                      <button
                        key={service.id}
                        type="button"
                        onClick={() => handleServiceSelect(service.id)}
                        className={cn(
                          "tp-loaded relative p-4 rounded-xl border-2 transition-all duration-200 text-center hover:-translate-y-0.5 active:scale-[0.98] motion-reduce:transform-none",
                          selectedService === service.id
                            ? "border-primary bg-primary/5"
                            : "border-border hover:border-primary/50"
                        )}
                      >
                        <div className="w-10 h-10 rounded-lg mx-auto mb-2 flex items-center justify-center bg-primary/10">
                          <Sparkles className="w-5 h-5 text-primary" />
                        </div>
                        <span className="text-xs font-medium text-foreground block">{service.name}</span>
                        <span className="text-xs text-muted-foreground block mt-1">
                          {service.is_quote_only ? "Get a quote" : formatMoney(service.price, currency, { from: true })}
                        </span>
                        {selectedService === service.id && (
                          <Check className="absolute top-2 right-2 w-4 h-4 text-primary" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Dynamic Form based on service type */}
              {selectedService && !isQuoteOnly && (
                <form onSubmit={handleBookingSubmit} className="space-y-8 border-t pt-8">
                  {/* Variant Selection (if service has variants) */}
                  {hasVariants && (
                    <div className="space-y-3">
                      <Label className="text-sm font-medium">Select option</Label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {selectedServiceData?.variants?.map((variant) => (
                          <button
                            key={variant.id}
                            type="button"
                            onClick={() => setSelectedVariant(variant.id)}
                            className={cn(
                              "relative p-4 rounded-xl border-2 transition-all duration-200 text-left",
                              selectedVariant === variant.id
                                ? "border-primary bg-primary/5"
                                : "border-border hover:border-primary/50"
                            )}
                          >
                            <span className="text-sm font-medium text-foreground block">{variant.name}</span>
                            <span className="text-xs text-muted-foreground block mt-1">{variant.duration}</span>
                            <span className="text-sm font-semibold text-primary block mt-2">{formatMoney(variant.price, currency)}</span>
                            {selectedVariant === variant.id && (
                              <Check className="absolute top-2 right-2 w-4 h-4 text-primary" />
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Date & Time */}
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Select date</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal h-11 rounded-xl",
                              !date && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {date ? format(date, "PPP") : "Pick a date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={date}
                            onSelect={setDate}
                            // Disable days before "today" in the tenant's timezone (not the
                            // browser's), so a far-away visitor can't book a day already past
                            // for the business — nor be blocked from one still valid for it.
                            disabled={(day) => format(day, 'yyyy-MM-dd') < tenantNow.todayStr}
                            initialFocus
                            className="pointer-events-auto rounded-xl"
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Select time</Label>
                      <Select value={time} onValueChange={setTime} disabled={!date}>
                        <SelectTrigger className="h-11 rounded-xl">
                          <SelectValue placeholder={!date ? "Select a date first" : availableTimeSlots.length === 0 ? "No slots available" : "Choose time"}>
                            {time && (
                              <span className="flex items-center gap-2">
                                <Clock className="w-4 h-4" />
                                {time}
                              </span>
                            )}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {availableTimeSlots.length === 0 ? (
                            <div className="px-3 py-2 text-sm text-muted-foreground">
                              No available slots for this date
                            </div>
                          ) : (
                            availableTimeSlots.map((slot) => (
                              <SelectItem key={slot} value={slot}>
                                {slot}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                      {date && availableTimeSlots.length === 0 && (
                        <p className="text-xs text-muted-foreground">
                          We're not available on this date. Please choose another day.
                        </p>
                      )}
                    </div>
                  </div>

                  {/* How often: a regular choice books a repeat clean. Chips, not a
                      select, so the time picker stays the form's only combobox. */}
                  <div className="space-y-2">
                    <Label id="booking-frequency-label" className="text-sm font-medium">How often?</Label>
                    <div role="group" aria-labelledby="booking-frequency-label" className="flex flex-wrap gap-2">
                      {BOOKING_FREQUENCIES.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          aria-pressed={formData.frequency === option.value}
                          onClick={() => setFormData((prev) => ({ ...prev, frequency: option.value }))}
                          className={cn(
                            "rounded-full border-2 px-4 py-2 text-sm font-medium transition-colors",
                            formData.frequency === option.value
                              ? "border-primary bg-primary/5 text-foreground"
                              : "border-border text-muted-foreground hover:border-primary/50 hover:text-foreground"
                          )}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                    {formData.frequency !== "one-off" && (
                      <p className="text-xs text-muted-foreground">
                        Same day and time each visit, starting on the date you've picked. We'll confirm before anything is booked.
                      </p>
                    )}
                  </div>

                  {/* Contact Info */}
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="booking-name" className="text-sm font-medium">Full name *</Label>
                      <Input
                        id="booking-name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="h-11 rounded-xl"
                        placeholder="Your name"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="booking-email" className="text-sm font-medium">Email address *</Label>
                      <Input
                        id="booking-email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="h-11 rounded-xl"
                        placeholder="your@email.com"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="booking-phone" className="text-sm font-medium">Phone number *</Label>
                      <Input
                        id="booking-phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="h-11 rounded-xl"
                        placeholder="Your phone"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="booking-address" className="text-sm font-medium">Property address *</Label>
                      <AddressAutocomplete
                        id="booking-address"
                        name="address"
                        value={formData.address}
                        onChange={handleAddressChange}
                        onPlaceSelect={handlePlaceSelect}
                        className="h-11 rounded-xl"
                        placeholder="Where should we come?"
                      />
                    </div>
                  </div>

                  {addressOutsideArea && (
                    <Alert className="border-amber-200 bg-amber-50">
                      <MapPin className="h-4 w-4 text-amber-600" />
                      <AlertDescription className="text-amber-800 text-sm">
                        We don't currently operate in this area, but we're expanding! Please submit your request and we'll let you know when we can help.
                      </AlertDescription>
                    </Alert>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="booking-message" className="text-sm font-medium">Specific notes</Label>
                    <Textarea
                      id="booking-message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      className="min-h-[110px] rounded-xl"
                      placeholder="Anything the cleaner should know? Access details, pets, priority areas..."
                    />
                  </div>

                  {/* Pay to book: only the ways this business takes payment, with what's due now */}
                  {offeredPaymentMethods.length > 0 && (
                    <div className="space-y-3 pt-2">
                      <Label id="booking-payment-label" className="text-sm font-medium">How would you like to pay?</Label>
                      <RadioGroup
                        aria-labelledby="booking-payment-label"
                        value={chosenPaymentMethod ?? ''}
                        onValueChange={(v) => setPaymentChoice(v as BookingPayMethod)}
                        className="grid gap-2"
                      >
                        {offeredPaymentMethods.includes('card') && (
                          <label className="flex items-center gap-3 p-3 border rounded-xl cursor-pointer hover:bg-secondary/50 has-[[data-state=checked]]:border-primary/50 has-[[data-state=checked]]:bg-primary/5">
                            <RadioGroupItem value="card" id="pm-card" aria-label="Pay by card now" />
                            <CreditCard className="w-5 h-5 text-muted-foreground" aria-hidden />
                            <div className="flex-1">
                              <div className="font-medium text-sm">Pay by card now</div>
                              <div className="text-xs text-muted-foreground" data-testid="pm-card-amount">
                                {amountDueSummary('card', bookingAmounts, currencySymbol)}. Your booking is made once you've paid.
                              </div>
                            </div>
                          </label>
                        )}
                        {offeredPaymentMethods.includes('paypal') && (
                          <label className="flex items-center gap-3 p-3 border rounded-xl cursor-pointer hover:bg-secondary/50 has-[[data-state=checked]]:border-primary/50 has-[[data-state=checked]]:bg-primary/5">
                            <RadioGroupItem value="paypal" id="pm-paypal" aria-label="PayPal" />
                            <Wallet className="w-5 h-5 text-muted-foreground" aria-hidden />
                            <div className="flex-1">
                              <div className="font-medium text-sm">PayPal</div>
                              <div className="text-xs text-muted-foreground">
                                Pay securely with PayPal after you book: {amountDueSummary('paypal', bookingAmounts, currencySymbol)}
                              </div>
                            </div>
                          </label>
                        )}
                        {offeredPaymentMethods.includes('cash') && (
                          <label className="flex items-center gap-3 p-3 border rounded-xl cursor-pointer hover:bg-secondary/50 has-[[data-state=checked]]:border-primary/50 has-[[data-state=checked]]:bg-primary/5">
                            <RadioGroupItem value="cash" id="pm-cash" aria-label="Pay cash on the day" />
                            <Banknote className="w-5 h-5 text-muted-foreground" aria-hidden />
                            <div className="flex-1">
                              <div className="font-medium text-sm">Pay cash on the day</div>
                              <div className="text-xs text-muted-foreground">
                                {amountDueSummary('cash', bookingAmounts, currencySymbol)}, paid to your cleaner.
                              </div>
                            </div>
                          </label>
                        )}
                      </RadioGroup>
                    </div>
                  )}

                  {/* Cancellation & refund policy — shown when the org has set one */}
                  {paymentPolicy?.cancellation_policy && (
                    <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-2">
                      <p className="text-sm font-medium text-foreground">Cancellation &amp; refund policy</p>
                      <p className="text-sm text-muted-foreground">{paymentPolicy.cancellation_policy}</p>
                      {paymentPolicy.refund_window_hours > 0 && (
                        <p className="text-sm text-muted-foreground">
                          Full refund when cancelled at least {paymentPolicy.refund_window_hours} hour{paymentPolicy.refund_window_hours === 1 ? '' : 's'} before your appointment.
                        </p>
                      )}
                      {paymentPolicy.refund_policy && (
                        <p className="text-sm text-muted-foreground">{paymentPolicy.refund_policy}</p>
                      )}
                    </div>
                  )}

                  <Button type="submit" variant="hero" size="lg" className="w-full" disabled={isBookingSubmitting}>
                    {isBookingSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    {bookingSubmitLabel(chosenPaymentMethod, bookingAmounts, currencySymbol)}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                  <p className="text-center text-sm text-muted-foreground" data-testid="booking-mode-note">
                    {chosenPaymentMethod === 'card'
                      ? "You'll pay securely on the next page. We hold your slot for 30 minutes while you do."
                      : bookingModeNote(confirmationMode)}
                  </p>
                </form>
              )}

              {/* Quote Form for quote-only services */}
              {selectedService && isQuoteOnly && (
                <form onSubmit={handleQuoteSubmit} className="space-y-6 border-t pt-8">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="quote-name" className="text-sm font-medium">Full name *</Label>
                      <Input
                        id="quote-name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="h-11 rounded-xl"
                        placeholder="Your name"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="quote-email" className="text-sm font-medium">Email address *</Label>
                      <Input
                        id="quote-email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="h-11 rounded-xl"
                        placeholder="your@email.com"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="quote-phone" className="text-sm font-medium">Phone number *</Label>
                      <Input
                        id="quote-phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="h-11 rounded-xl"
                        placeholder="Your phone"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="quote-address" className="text-sm font-medium">Property address *</Label>
                      <AddressAutocomplete
                        id="quote-address"
                        name="address"
                        value={formData.address}
                        onChange={handleAddressChange}
                        className="h-11 rounded-xl"
                        placeholder="Property address"
                      />
                    </div>
                  </div>

                  {addressOutsideArea && (
                    <Alert className="border-amber-200 bg-amber-50">
                      <MapPin className="h-4 w-4 text-amber-600" />
                      <AlertDescription className="text-amber-800 text-sm">
                        We don't currently operate in this area, but we're expanding! Please submit your request and we'll let you know when we can help.
                      </AlertDescription>
                    </Alert>
                  )}

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Frequency</Label>
                    <Select value={formData.frequency} onValueChange={(v) => setFormData(prev => ({ ...prev, frequency: v }))}>
                      <SelectTrigger className="h-11 rounded-xl">
                        <SelectValue placeholder="Select frequency" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="one-off">One-off clean</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="fortnightly">Fortnightly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="quote-sqm" className="text-sm font-medium">Approximate size (sqm)</Label>
                    <Input
                      id="quote-sqm"
                      name="sqm"
                      type="number"
                      value={formData.sqm}
                      onChange={handleInputChange}
                      className="h-11 rounded-xl"
                      placeholder="e.g. 150 (optional)"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="quote-message" className="text-sm font-medium">Additional details</Label>
                    <Textarea
                      id="quote-message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      className="min-h-[120px] rounded-xl"
                      placeholder="Tell us about your requirements..."
                    />
                  </div>

                  <Button type="submit" variant="hero" size="lg" className="w-full">
                    <Send className="w-4 h-4 mr-2" />
                    Get my free quote
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Contact */}
      <section className="tp-section--tight tp-tint">
        <div className="container mx-auto px-5 md:px-6">
          <div className="text-center max-w-lg mx-auto">
            <h2 className="tp-h3 mb-3 text-[clamp(1.5rem,1.2rem+1vw,2rem)]">{renderDisplayText("Need help choosing?")}</h2>
            <p className="text-muted-foreground mb-6">
              {renderDisplayText(isDemoRoute ? DEMO_SHOWCASE.booking.helpText : "Our friendly team is here to help you find the perfect cleaning solution for your home or business.")}
            </p>
            <CallbackDialog variant="booking" />
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default BookingPage;
